import { Fragment, useState, useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  MenuItem,
  Popover,
} from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { fCurrencyFormat } from 'src/utils/format-number';
import { fConvertDateFormat } from 'src/utils/format-date';
import { AgingBucket, IAgingClient } from 'src/types';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { TableHeadCustom, type TableHeadCellProps } from 'src/components/table';
import AgingBucketBadge from '../aging-bucket-badge';

type SortField = 'clientName' | 'totalOverdue' | 'invoiceCount';
type SortOrder = 'asc' | 'desc';

type Props = {
  clients: IAgingClient[];
  activeBucket: AgingBucket | null;
};

export default function ClientAgingTable({ clients, activeBucket }: Props) {
  const { t, currentLang } = useLocales('operativaDashboard');
  const router = useRouter();

  const [expandedClientId, setExpandedClientId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>('totalOverdue');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuClientSlug, setMenuClientSlug] = useState<string | null>(null);

  const TABLE_HEAD: TableHeadCellProps[] = useMemo(
    () => [
      { field: 'expand', width: 40 },
      { field: 'clientName', title: t('cliente') },
      { field: 'totalOverdue', title: t('totale_scaduto'), align: 'right' },
      { field: 'worstBucket', title: t('bucket_peggiore') },
      { field: 'invoiceCount', title: t('n_fatture'), align: 'right' },
      { field: 'actions', title: t('azioni'), align: 'right' },
    ],
    [t],
  );

  const filteredClients = useMemo(() => {
    if (!clients?.length) return [];
    let result = clients;
    if (activeBucket) {
      result = clients.filter((c) => c.worstBucket === activeBucket);
    }
    return [...result].sort((a, b) => {
      const multiplier = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'clientName') return multiplier * a.clientName.localeCompare(b.clientName);
      if (sortField === 'totalOverdue') return multiplier * (a.totalOverdue - b.totalOverdue);
      if (sortField === 'invoiceCount') return multiplier * (a.invoiceCount - b.invoiceCount);
      return 0;
    });
  }, [clients, activeBucket, sortField, sortOrder]);

  const handleSort = (field: string) => {
    const sortable: string[] = ['clientName', 'totalOverdue', 'invoiceCount'];
    if (!sortable.includes(field)) return;
    const typed = field as SortField;
    if (sortField === typed) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(typed);
      setSortOrder('desc');
    }
  };

  const handleToggleExpand = (clientId: number) => {
    setExpandedClientId(expandedClientId === clientId ? null : clientId);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, clientSlug: string) => {
    setMenuAnchor(event.currentTarget);
    setMenuClientSlug(clientSlug);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuClientSlug(null);
  };

  return (
    <Card>
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHeadCustom
            order={sortOrder}
            orderBy={sortField}
            headLabel={TABLE_HEAD}
            onSort={handleSort}
          />
          <TableBody>
            {filteredClients.map((client) => {
              const isExpanded = expandedClientId === client.clientId;

              return (
                <Fragment key={client.clientId}>
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleToggleExpand(client.clientId)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        <Iconify
                          icon={
                            isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'
                          }
                          width={16}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(paths.admin.clients.details(client.clientSlug));
                          }}
                        >
                          {client.clientName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {client.clientSlug}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                        {fCurrencyFormat(client.totalOverdue, currentLang.value)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AgingBucketBadge bucket={client.worstBucket} />
                    </TableCell>
                    <TableCell align="right">{client.invoiceCount}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, client.clientSlug);
                        }}
                      >
                        <Iconify icon="eva:more-vertical-fill" width={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 6, backgroundColor: 'background.neutral' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }}>
                                  {t('codice_ordine')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }} align="right">
                                  {t('importo')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }}>
                                  {t('data_scadenza')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'fontWeightBold' }} align="right">
                                  {t('giorni_ritardo')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(client.invoices ?? []).map((invoice) => (
                                <TableRow key={invoice.orderCode}>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      onClick={() =>
                                        router.push(
                                          paths.admin.orderTypes.clientOrders.details(
                                            invoice.orderCode,
                                          ),
                                        )
                                      }
                                    >
                                      {invoice.orderCode}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    {fCurrencyFormat(invoice.totalEndPrice, currentLang.value)}
                                  </TableCell>
                                  <TableCell>
                                    {fConvertDateFormat(invoice.invoiceDueDate)}
                                  </TableCell>
                                  <TableCell align="right">{invoice.daysOverdue}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (menuClientSlug) {
              router.push(paths.admin.clients.details(menuClientSlug));
            }
            handleMenuClose();
          }}
        >
          <Iconify icon="eva:eye-fill" width={20} sx={{ mr: 1 }} />
          {t('vedi_fatture')}
        </MenuItem>
      </Popover>
    </Card>
  );
}
